import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Users, Plus, Pencil, Trash2, Search, AlertTriangle, Phone, Wallet, RefreshCw, Eye, Upload, X, Camera } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Helper to get worker photo URL - use direct static file path
const getWorkerPhotoUrl = (worker) => {
  if (worker?.photo_path) {
    return `${BACKEND_URL}/${worker.photo_path}`;
  }
  return null;
};

const WorkersPage = () => {
  const { token, isAdmin } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenewDialogOpen, setIsRenewDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [renewField, setRenewField] = useState('');
  const [renewDate, setRenewDate] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    residency_number: '',
    residency_expiry: '',
    salary: '',
    contract_start: '',
    contract_end: '',
    nationality: '',
    job_title: '',
    phone: '',
    notes: ''
  });

  const fetchWorkers = async () => {
    try {
      const response = await axios.get(`${API}/workers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkers(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkers();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData, salary: parseFloat(formData.salary) };
      let workerId = selectedWorker?.id;
      
      if (selectedWorker) {
        await axios.put(`${API}/workers/${selectedWorker.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('تم تحديث بيانات العامل بنجاح');
      } else {
        const response = await axios.post(`${API}/workers`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        workerId = response.data.id;
        toast.success('تم إضافة العامل بنجاح');
      }
      
      // Upload photo if selected
      if (selectedPhoto && workerId) {
        setUploadingPhoto(true);
        const photoFormData = new FormData();
        photoFormData.append('file', selectedPhoto);
        try {
          await axios.post(`${API}/workers/${workerId}/upload-photo`, photoFormData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success('تم رفع الصورة بنجاح');
        } catch (photoError) {
          toast.error('خطأ في رفع الصورة');
        }
        setUploadingPhoto(false);
      }
      
      setIsDialogOpen(false);
      fetchWorkers();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/workers/${selectedWorker.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف العامل بنجاح');
      setIsDeleteDialogOpen(false);
      fetchWorkers();
    } catch (error) {
      toast.error('خطأ في حذف العامل');
    }
  };

  const openRenewDialog = (worker, field) => {
    setSelectedWorker(worker);
    setRenewField(field);
    setRenewDate('');
    setIsRenewDialogOpen(true);
  };

  const handleRenew = async () => {
    if (!renewDate) {
      toast.error('يرجى إدخال التاريخ الجديد');
      return;
    }
    try {
      const updateData = { ...selectedWorker, [renewField]: renewDate };
      await axios.put(`${API}/workers/${selectedWorker.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم التجديد بنجاح');
      setIsRenewDialogOpen(false);
      fetchWorkers();
    } catch (error) {
      toast.error('خطأ في التجديد');
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      residency_expiry: 'الإقامة',
      contract_end: 'العقد'
    };
    return labels[field] || field;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      residency_number: '',
      residency_expiry: '',
      salary: '',
      contract_start: '',
      contract_end: '',
      nationality: '',
      job_title: '',
      phone: '',
      notes: ''
    });
    setSelectedWorker(null);
    setSelectedPhoto(null);
    setPhotoPreview(null);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('يجب أن تكون الصورة بصيغة JPG أو PNG أو WebP');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('حجم الصورة يجب أن لا يتجاوز 5 ميجابايت');
        return;
      }
      setSelectedPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const clearPhotoSelection = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getWorkerPhotoUrl = (worker) => {
    if (worker?.photo_path) {
      return `${API}/workers/${worker.id}/photo`;
    }
    return null;
  };

  const openEditDialog = (worker) => {
    setSelectedWorker(worker);
    setFormData({
      name: worker.name,
      residency_number: worker.residency_number,
      residency_expiry: worker.residency_expiry,
      salary: worker.salary.toString(),
      contract_start: worker.contract_start,
      contract_end: worker.contract_end,
      nationality: worker.nationality || '',
      job_title: worker.job_title || '',
      phone: worker.phone || '',
      notes: worker.notes || ''
    });
    setIsDialogOpen(true);
  };

  const openViewDialog = (worker) => {
    setSelectedWorker(worker);
    setIsViewDialogOpen(true);
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const expiry = new Date(dateStr);
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diff <= 30 && diff >= 0;
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const expiry = new Date(dateStr);
    return expiry < today;
  };

  const filteredWorkers = workers.filter(worker =>
    (worker.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (worker.residency_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (worker.job_title || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">العمالة المنزلية</h1>
          <p className="text-slate-500 mt-1">إدارة بيانات العمالة المنزلية</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="text-white" style={{ background: '#1a365d' }}
            data-testid="add-worker-btn"
          >
            <Plus className="w-4 h-4 ml-2" />
            إضافة عامل
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="البحث عن عامل..."
          className="pr-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-workers"
        />
      </div>

      {/* Workers Table */}
      <Card className="border-0 shadow-sm" data-testid="workers-table-card">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {isAdmin && <TableHead className="text-center w-24">إجراءات</TableHead>}
                  <TableHead className="text-center">استعراض</TableHead>
                  <TableHead className="text-center">انتهاء العقد</TableHead>
                  <TableHead className="text-center">الراتب</TableHead>
                  <TableHead className="text-center">انتهاء الإقامة</TableHead>
                  <TableHead className="text-center">رقم الإقامة</TableHead>
                  <TableHead className="text-right">العامل</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 7 : 6} className="text-center py-8 text-slate-400">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>لا يوجد عمالة</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredWorkers.map((worker) => (
                    <TableRow key={worker.id} className="table-row-hover" data-testid={`worker-row-${worker.id}`}>
                      {isAdmin && (
                        <TableCell className="text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(worker)}
                              data-testid={`edit-worker-${worker.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => { setSelectedWorker(worker); setIsDeleteDialogOpen(true); }}
                              data-testid={`delete-worker-${worker.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-blue-600 hover:text-blue-700"
                          onClick={() => openViewDialog(worker)}
                          data-testid={`view-worker-${worker.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-2 justify-center flex-wrap">
                          <span className="number text-sm">{worker.contract_end}</span>
                          {isExpired(worker.contract_end) && (
                            <>
                              <Badge variant="destructive" className="text-xs">منتهي</Badge>
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                  onClick={() => openRenewDialog(worker, 'contract_end')}
                                >
                                  <RefreshCw className="w-3 h-3 ml-1" />
                                  تجديد
                                </Button>
                              )}
                            </>
                          )}
                          {isExpiringSoon(worker.contract_end) && !isExpired(worker.contract_end) && (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-1 justify-center">
                          <Wallet className="w-4 h-4 text-slate-400" />
                          <span className="number">{worker.salary.toLocaleString()}</span>
                          <span className="text-xs text-slate-400">ر.س</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-2 justify-center flex-wrap">
                          <span className="number text-sm">{worker.residency_expiry}</span>
                          {isExpired(worker.residency_expiry) && (
                            <>
                              <Badge variant="destructive" className="text-xs">منتهية</Badge>
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                  onClick={() => openRenewDialog(worker, 'residency_expiry')}
                                >
                                  <RefreshCw className="w-3 h-3 ml-1" />
                                  تجديد
                                </Button>
                              )}
                            </>
                          )}
                          {isExpiringSoon(worker.residency_expiry) && !isExpired(worker.residency_expiry) && (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium number">{worker.residency_number}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-3 justify-end">
                          <div className="text-right">
                            <p className="font-medium text-slate-900">{worker.name}</p>
                            <p className="text-sm text-slate-500">{worker.job_title || worker.nationality}</p>
                          </div>
                          {worker.photo_path ? (
                            <img
                              src={getWorkerPhotoUrl(worker)}
                              alt={worker.name}
                              className="w-10 h-10 rounded-xl object-cover border-2 border-purple-100"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center"
                            style={{ display: worker.photo_path ? 'none' : 'flex' }}
                          >
                            <Users className="w-5 h-5 text-purple-600" />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle>{selectedWorker ? 'تعديل بيانات العامل' : 'إضافة عامل جديد'}</DialogTitle>
            <DialogDescription>أدخل بيانات العامل بالكامل</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>الاسم</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="worker-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>الجنسية</Label>
                <Input
                  value={formData.nationality}
                  onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                  data-testid="worker-nationality-input"
                />
              </div>
              <div className="space-y-2">
                <Label>المسمى الوظيفي</Label>
                <Input
                  value={formData.job_title}
                  onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  placeholder="سائق، خادمة..."
                  data-testid="worker-job-input"
                />
              </div>
              <div className="space-y-2">
                <Label>رقم الهاتف</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="worker-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>رقم الإقامة</Label>
                <Input
                  value={formData.residency_number}
                  onChange={(e) => setFormData({ ...formData, residency_number: e.target.value })}
                  required
                  data-testid="worker-residency-input"
                />
              </div>
              <div className="space-y-2">
                <Label>تاريخ انتهاء الإقامة</Label>
                <Input
                  type="date"
                  value={formData.residency_expiry}
                  onChange={(e) => setFormData({ ...formData, residency_expiry: e.target.value })}
                  required
                  data-testid="worker-residency-expiry-input"
                />
              </div>
              <div className="space-y-2">
                <Label>الراتب (ر.س)</Label>
                <Input
                  type="number"
                  value={formData.salary}
                  onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                  required
                  data-testid="worker-salary-input"
                />
              </div>
              <div className="space-y-2">
                <Label>تاريخ بداية العقد</Label>
                <Input
                  type="date"
                  value={formData.contract_start}
                  onChange={(e) => setFormData({ ...formData, contract_start: e.target.value })}
                  required
                  data-testid="worker-contract-start-input"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>تاريخ انتهاء العقد</Label>
                <Input
                  type="date"
                  value={formData.contract_end}
                  onChange={(e) => setFormData({ ...formData, contract_end: e.target.value })}
                  required
                  data-testid="worker-contract-end-input"
                />
              </div>
              
              {/* Photo Upload Section */}
              <div className="space-y-2 col-span-2">
                <Label>صورة العامل</Label>
                <div className="flex items-center gap-4">
                  {/* Photo Preview */}
                  <div className="relative">
                    {photoPreview ? (
                      <div className="relative">
                        <img
                          src={photoPreview}
                          alt="Preview"
                          className="w-20 h-20 rounded-xl object-cover border-2 border-purple-200"
                        />
                        <button
                          type="button"
                          onClick={clearPhotoSelection}
                          className="absolute -top-2 -left-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : selectedWorker?.photo_path ? (
                      <img
                        src={getWorkerPhotoUrl(selectedWorker)}
                        alt={selectedWorker.name}
                        className="w-20 h-20 rounded-xl object-cover border-2 border-purple-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-20 h-20 bg-purple-50 rounded-xl flex items-center justify-center border-2 border-dashed border-purple-200">
                        <Camera className="w-8 h-8 text-purple-300" />
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Button */}
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoSelect}
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      data-testid="worker-photo-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                      disabled={uploadingPhoto}
                    >
                      <Upload className="w-4 h-4 ml-2" />
                      {uploadingPhoto ? 'جاري الرفع...' : 'اختر صورة'}
                    </Button>
                    <p className="text-xs text-slate-400 mt-1">
                      JPG, PNG أو WebP - الحد الأقصى 5 ميجابايت
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 col-span-2">
                <Label>ملاحظات</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="ملاحظات إضافية..."
                  data-testid="worker-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                إلغاء
              </Button>
              <Button type="submit" className="text-white" style={{ background: '#1a365d' }} data-testid="save-worker-btn">
                {selectedWorker ? 'حفظ التعديلات' : 'إضافة العامل'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader className="text-right">
            <AlertDialogTitle className="text-right">تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription className="text-right">
              هل أنت متأكد من حذف بيانات هذا العامل؟ لا يمكن التراجع عن هذا الإجراء.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-worker"
            >
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Renew Dialog */}
      <Dialog open={isRenewDialogOpen} onOpenChange={setIsRenewDialogOpen}>
        <DialogContent className="max-w-sm" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" style={{ color: '#D4AF37' }} />
              تجديد {getFieldLabel(renewField)}
            </DialogTitle>
            <DialogDescription>
              أدخل تاريخ الانتهاء الجديد لـ{getFieldLabel(renewField)}
            </DialogDescription>
          </DialogHeader>
          {selectedWorker && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">العامل</p>
                <p className="font-medium" style={{ color: '#1a365d' }}>
                  {selectedWorker.name} - {selectedWorker.job_title || selectedWorker.nationality}
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <p className="text-sm text-slate-500">التاريخ الحالي (منتهي)</p>
                <p className="font-medium text-red-600 number">{selectedWorker[renewField]}</p>
              </div>
              <div className="space-y-2">
                <Label>تاريخ الانتهاء الجديد</Label>
                <Input
                  type="date"
                  value={renewDate}
                  onChange={(e) => setRenewDate(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsRenewDialogOpen(false)}>
              إلغاء
            </Button>
            <Button 
              onClick={handleRenew}
              className="text-white bg-emerald-600 hover:bg-emerald-700"
            >
              <RefreshCw className="w-4 h-4 ml-2" />
              تأكيد التجديد
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Worker Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" style={{ color: '#8B5CF6' }} />
              تفاصيل العامل
            </DialogTitle>
          </DialogHeader>
          {selectedWorker && (
            <div className="space-y-4">
              {/* Worker Photo */}
              <div className="flex justify-center">
                {selectedWorker.photo_path ? (
                  <img
                    src={getWorkerPhotoUrl(selectedWorker)}
                    alt={selectedWorker.name}
                    className="w-24 h-24 rounded-2xl object-cover border-4 border-purple-100 shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="w-24 h-24 bg-purple-100 rounded-2xl flex items-center justify-center"
                  style={{ display: selectedWorker.photo_path ? 'none' : 'flex' }}
                >
                  <Users className="w-12 h-12 text-purple-400" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">الاسم</p>
                  <p className="font-medium" style={{ color: '#1a365d' }}>{selectedWorker.name}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">الجنسية</p>
                  <p className="font-medium">{selectedWorker.nationality || '-'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">المهنة</p>
                  <p className="font-medium">{selectedWorker.job_title || '-'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">رقم الإقامة</p>
                  <p className="font-medium number">{selectedWorker.residency_number}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">انتهاء الإقامة</p>
                  <p className="font-medium number">{selectedWorker.residency_expiry}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">الراتب</p>
                  <p className="font-medium number">{selectedWorker.salary?.toLocaleString()} ر.س</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">بداية العقد</p>
                  <p className="font-medium number">{selectedWorker.contract_start}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">نهاية العقد</p>
                  <p className="font-medium number">{selectedWorker.contract_end}</p>
                </div>
                {selectedWorker.phone && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">رقم الهاتف</p>
                    <p className="font-medium number">{selectedWorker.phone}</p>
                  </div>
                )}
                {selectedWorker.notes && (
                  <div className="p-3 bg-slate-50 rounded-lg col-span-2">
                    <p className="text-sm text-slate-500">ملاحظات</p>
                    <p className="font-medium">{selectedWorker.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkersPage;
